
import {Vue, Component, Prop, Watch} from "vue-property-decorator";
import {PropType} from "vue";
import {namespace} from "vuex-class";
import {IStudentEducation} from "@/interfaces/Student/IStudentEducation";
import {IAbibaroStatus} from "@/interfaces/Student/IAbibaroStatus";
import {ABIBARO_ENABLED} from "@/config/Features";
import {IStudent} from "@/interfaces/Student/IStudent";
import {IExamAndEducation} from "@/interfaces/Exam/IExamAndEducation";
import moment from "moment";
import {formatStudentName} from "@/utils/NameUtil";
import UserService from "@/services/UserService";
import ExaminerAIService from "@/services/ExaminerAIService";
import CustomSlider from "@/components/Slider/CustomSlider.vue";
import ExaminerFeedbackModal from "@/components/ExaminerAI/ExaminerFeedbackModal.vue";
import AboutExaminerModal from "@/components/ExaminerAI/AboutExaminerModal.vue";

const DrivingSchool = namespace("driving-school");
const SimulatorTenStarStudentModule = namespace("simulator-ten-star-student");
const SimulatorTenStarConfig = namespace("simulator-ten-star-config");

@Component({
  components: {CustomSlider, ExaminerFeedbackModal, AboutExaminerModal},
})
export default class StudentStatusCard extends Vue {
  public name = "StudentStatusCard";

  @Prop({type: Boolean, default: false})
  public examinerIsOn!: boolean;

  @Prop({type: Array as PropType<Array<IExamAndEducation>>, default: () => [], required: true})
  public examAndEducationList!: Array<IExamAndEducation>;

  @Prop({type: Array as PropType<Array<IStudentEducation>>})
  public studentEducations!: Array<IStudentEducation>;

  @Prop({type: Object as PropType<IAbibaroStatus>})
  public studentEducationAbibaroStatus!: IAbibaroStatus;

  @Prop({type: Object as PropType<IStudent>})
  public student!: IStudent;

  @Prop({type: Number})
  public learnStatus!: number;

  @Prop({type: Number})
  public activeLearnStatusIndex!: number;

  @Prop({type: Boolean})
  public registeredInAbibaro!: boolean;

  @Prop({type: Boolean})
  public isLoading!: boolean;

  private examinerFeedbackData = {
    student_education_id: "",
    prediction_id: "",
    instructor_agreement: "",
    name_surname: "",
    ds_name: "",
    earliest_th_exam: "",
    fsc_user: "",
  };

  private examinerAllowedEducations = ["B", "B197", "B78"];
  protected examinerFeedbackModal = "examinerFeedbackModal";
  protected aboutExaminerModal = "aboutExaminerModal";

  private examinerPredictionLoading = false;
  private examinerPrediction: any = 0;
  private examinerAllowFeedback = false;

  private hasTenstarSimulatorAccount = false;

  @SimulatorTenStarStudentModule.Action("checkHasAccount")
  public checkHasTenstarAccount: any;

  @DrivingSchool.Action("findOne")
  public examinerFindCurrentDS: any;

  @DrivingSchool.Getter("getDataItem")
  public examinerCurrentDS: any;

  @SimulatorTenStarConfig.Getter("getDataItem")
  public simulatorTenStarConfigCurrent: any;

  public get studentEducationId() {
    return this.studentEducations[this.activeLearnStatusIndex].id;
  }

  public get selectedExamAndEducation() {
    return this.examAndEducationList?.[this.activeLearnStatusIndex];
  }

  public get hasTenstarSimulatorAccess() {
    return this.simulatorTenStarConfigCurrent.applicationId && this.simulatorTenStarConfigCurrent.secretKey;
  }

  public examinerAgreementLevels = [
    {value: "agree", label: "Ich stimme dem KI-Piloten zu!"},
    {value: "disagree", label: "Ich stimme der Einschätzung nicht zu!"},
  ];

  private get studentId() {
    return this.student?.id;
  }

  private onLearnStatusRedirect(): void {
    if (this.studentId) {
      this.$router.push({
        name: "LearnStatus",
        params: {id: String(this.studentId)},
      });
    }
  }

  private onKiPilotPraxisRedirect(): void {
    if (this.studentId) {
      this.$router.push({
        name: "KiPilotPraxis",
        params: {id: String(this.studentId)},
      });
    }
  }

  private get showKiPilotPraxis() {
    return !!this.studentEducations?.[this.learnStatusIndexProxy]?.kiPilotPraxisEnabled;
  }

  private onAbibaroRedirect(): void {
    if (this.studentId) {
      this.$router.push({
        name: "StudentAbibaroCreate",
        params: {id: String(this.studentId)},
      });
    }
  }

  private onSimulatorRedirect(): void {
    if (this.studentId) {
      this.$router.push({
        name: "StudentSimulator",
        params: {id: String(this.studentId)},
      });
    }
  }

  private get learnStatusIndexProxy() {
    return this.activeLearnStatusIndex;
  }

  private set learnStatusIndexProxy(value: number) {
    this.$emit("update:activeLearnStatusIndex", value);
  }

  private get showAbibaro() {
    return ABIBARO_ENABLED && this.registeredInAbibaro;
  }

  private get studentEducationIsRegisteredInAbibaro() {
    return this.showAbibaro && this.studentEducationAbibaroStatus?.registeredInAbibaro;
  }

  private get studentEducationAbibaroProgressPercentages() {
    return this.studentEducationAbibaroStatus?.progressPercentages || 0;
  }

  // ---- ExaminerAI ----
  private get studentFullName(): string {
    if (this.student) {
      return formatStudentName(this.student?.firstName, this.student?.lastName);
    } else {
      return "";
    }
  }

  private get userName() {
    return UserService.getUser().username;
  }

  private get showExaminerAI() {
    if (!this.examinerIsOn) return false;

    // @ts-ignore
    const current = this.studentEducations[this.learnStatusIndexProxy]?.licenseClassWithKeyNumber;
    if (!current) return false;

    const keyNumber = current.keyNumber ? current.keyNumber : "";
    const licenseClass = current.licenseClass + keyNumber;
    const correctLicenceClass = this.examinerAllowedEducations.includes(licenseClass);

    return correctLicenceClass && this.hasUpcomingOrUnattemptedExam;
  }

  public async setEarliestEducationExamDate(studentEducationId: string | number): Promise<void> {
    this.examinerFeedbackData.earliest_th_exam = await ExaminerAIService.fetchEarliestEducationExam(studentEducationId);
  }

  private get hasUpcomingOrUnattemptedExam() {
    const theoryExamCount = this.selectedExamAndEducation?.theoryExamCount;
    const latestTheoryExamStatus = this.selectedExamAndEducation?.latestTheoryExamStatus;

    // has not done any exams
    if (theoryExamCount < 1) return true;

    // check if exam is 'Gemeldet'
    if (theoryExamCount === 1 && latestTheoryExamStatus.id === 1) {
      return true;
    }

    return false;
  }

  public examinerInitializeInferenceFeedbackData() {
    this.examinerFeedbackData.student_education_id = this.studentEducationId.toString();
    this.examinerFeedbackData.name_surname = this.studentFullName;
    this.examinerFeedbackData.fsc_user = this.userName;
    this.examinerFeedbackData.ds_name = this.examinerCurrentDS?.name || "";
  }

  // Request examiner prediction for studentEducation
  public async examinerHandleInference() {
    // Initialize necessary feedback data for identifying an examiner prediction
    this.examinerInitializeInferenceFeedbackData();

    const inferenceData = {
      student_education_id: this.examinerFeedbackData.student_education_id,
      name_surname: this.examinerFeedbackData.name_surname,
      ds_name: this.examinerFeedbackData.ds_name,
      fsc_user: this.examinerFeedbackData.fsc_user,
    };

    this.examinerPredictionLoading = true;
    try {
      const data = await ExaminerAIService.findPredictionForStudent(inferenceData);
      this.examinerPrediction = data.predicted_class;
      this.examinerFeedbackData = {
        ...this.examinerFeedbackData,
        prediction_id: data.prediction_id,
      };
      await this.setEarliestEducationExamDate(this.studentEducationId);
      await this.examinerCheckFeedbackEligibility();
    } catch (e) {
      const errorMessage = e?.response?.data?.error_message;
      if (errorMessage) {
        console.log("ExaminerAI:", errorMessage);
        this.$toasted.error("Nicht genügend Daten vorhanden");
      }
      this.examinerPrediction = -1;
    }
    this.examinerPredictionLoading = false;
  }

  // True when a feedback has not yet been submitted
  public async examinerCheckFeedbackEligibility() {
    this.examinerPredictionLoading = true;
    try {
      const data = await ExaminerAIService.checkFeedbackEligibility(this.studentEducationId);
      this.examinerAllowFeedback = data.eligibility === "true";
    } catch (e) {
      const errorMessage = e?.response?.data?.message;
      this.$toasted.error(errorMessage);
    }
    this.examinerPredictionLoading = false;
  }

  // Submits user feedback on examiners prediction
  public async examinerHandleFeedbackSubmit() {
    this.examinerPredictionLoading = true;
    try {
      const {message} = await ExaminerAIService.submitUserFeedback(this.examinerFeedbackData);
      await this.examinerCheckFeedbackEligibility();
      console.log("ExaminerAI:", message);
      this.$toasted.success("Danke! Deine Einschätzung hilft uns, den KI-Piloten noch besser zu machen.");
    } catch (e) {
      const errorMessage = e?.response?.data?.message || "Fehler beim Senden des Feedbacks";
      this.$toasted.error(errorMessage);
    }
    this.examinerPredictionLoading = false;
  }

  private get examinerInstructorAgreementObject() {
    return this.examinerAgreementLevels.find((opt) => opt.value === this.examinerFeedbackData.instructor_agreement) || null;
  }

  private set examinerInstructorAgreementObject(selected: { value: string; label: string } | null) {
    this.examinerFeedbackData.instructor_agreement = selected?.value || "";
  }

  public examinerResetInstructorAnswer() {
    this.examinerFeedbackData.instructor_agreement = "";
  }

  public examinerResetFeedbackAnswer() {
    this.examinerFeedbackData.student_education_id = "";
    this.examinerFeedbackData.instructor_agreement = "";
    this.examinerFeedbackData.prediction_id = "";
    this.examinerFeedbackData.earliest_th_exam = "";
    this.examinerFeedbackData.name_surname = "";
    this.examinerFeedbackData.ds_name = "";
    this.examinerFeedbackData.fsc_user = "";
  }

  public examinerShowAboutModal() {
    this.$bvModal.show(this.aboutExaminerModal);
  }

  public get examinerInstructorAgreement() {
    return this.examinerFeedbackData?.instructor_agreement;
  }

  public examinerSubmitFeedback() {
    const now = moment();
    this.examinerHandleFeedbackSubmit();
  }

  public async mounted(): Promise<void> {
    await this.examinerFindCurrentDS({
      resource: "driving-schools",
      id: "current",
      descriptionField: "name",
    });
  }

  @Watch("examinerInstructorAgreement", {immediate: true})
  onAnswered() {
    if (this.examinerInstructorAgreement) {
      this.$bvModal.show(this.examinerFeedbackModal);
    }
  }

  @Watch("learnStatusIndexProxy", {immediate: false, deep: false})
  onEducationClassChanged() {
    this.examinerResetFeedbackAnswer();
    this.examinerPrediction = 0;
  }

  @Watch("student", {immediate: false, deep: false})
  async onStudentChanged() {
    this.examinerResetFeedbackAnswer();
    this.examinerPrediction = 0;

    this.hasTenstarSimulatorAccount = await this.checkHasTenstarAccount({studentId: this.studentId});
  }
}
